import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, History, KeyRound, Pencil } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { AdminFormDialog } from "../components/admins/AdminFormDialog";
import { CredentialDialog } from "../components/admins/CredentialDialog";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";

function fmtTime(ts) {
  if (!ts) return "—";
  try { return new Date(ts * 1000).toLocaleString(); } catch { return "—"; }
}

export default function AdminManagement() {
  const [catalog, setCatalog] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cred, setCred] = useState(null); // { username, password, title }
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  const roleLabel = useCallback((key) => catalog?.role_templates?.[key]?.label || (key ? key : "Custom"), [catalog]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get("/admins");
      setRows(data.items);
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not load admins."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    apiClient.get("/admins/permission-catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    load();
  }, [load]);

  const onSaved = (created) => {
    load();
    if (created?.new_password) {
      setCred({ username: created.username, password: created.new_password, title: "Admin created" });
    }
  };

  const doReset = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const { data } = await apiClient.post(`/admins/${resetTarget.id}/reset-password`, {});
      setCred({ username: data.username, password: data.new_password, title: "Password reset" });
      toast.success("Password reset. Existing sessions were signed out.");
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not reset password."));
    } finally {
      setResetting(false);
      setResetTarget(null);
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    try {
      const { data } = await apiClient.get("/admins/audit?limit=100");
      setAudit(data.items);
    } catch { setAudit([]); }
  };

  const columns = [
    { key: "name", header: "Name", primary: true, render: (r) => r.full_name || r.username },
    { key: "username", header: "Username", subtitle: true, render: (r) => <span className="font-mono">{r.username}</span> },
    { key: "role", header: "Role", render: (r) => r.is_super_admin ? "Super Admin" : roleLabel(r.role_template) },
    {
      key: "status", header: "Status", badge: true,
      render: (r) => (
        <div className="flex flex-wrap gap-1.5">
          <StatusBadge label={r.active ? "Active" : "Inactive"} variant={r.active ? "success" : "deactivated"} />
          {r.is_super_admin && <StatusBadge label="Super" variant="info" />}
        </div>
      ),
    },
    {
      key: "actions", header: "Actions", footer: true,
      render: (r) => (
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" onClick={() => { setEditing(r); setFormOpen(true); }} data-testid={`admin-edit-${r.id}`}>
            <Pencil size={14} strokeWidth={1.5} /> Edit
          </Button>
          <Button size="sm" variant="outline" onClick={() => setResetTarget(r)} data-testid={`admin-reset-${r.id}`}>
            <KeyRound size={14} strokeWidth={1.5} /> Reset Password
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="admin-management-page">
      <PageHeader
        title="Admin Management"
        description="Create admins, assign roles and permissions, and reset credentials."
        testId="admin-management-header"
        actions={
          <>
            <Button variant="outline" onClick={openAudit} data-testid="admin-audit-button">
              <History size={16} strokeWidth={1.5} /> Audit History
            </Button>
            <Button variant="accent" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="admin-create-button">
              <Plus size={16} strokeWidth={1.5} /> Create Admin
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        error={error}
        onRetry={load}
        emptyTitle="No admins yet"
        emptyDescription="Create your first admin to delegate access."
        testId="admin-management-table"
      />

      <AdminFormDialog open={formOpen} onOpenChange={setFormOpen} onSaved={onSaved} admin={editing} catalog={catalog} />

      <CredentialDialog
        open={!!cred}
        onOpenChange={(v) => !v && setCred(null)}
        username={cred?.username}
        password={cred?.password}
        title={cred?.title}
      />

      <ConfirmDialog
        open={!!resetTarget}
        onOpenChange={(v) => !v && setResetTarget(null)}
        title="Reset this admin's password?"
        description={`A new temporary password will be generated for ${resetTarget?.username || "this admin"}. Their current password and all active sessions will stop working immediately. You'll see the new password once to copy.`}
        confirmLabel="Reset Password"
        variant="default"
        loading={resetting}
        onConfirm={doReset}
        testId="admin-reset-confirm-dialog"
      />

      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto" data-testid="admin-audit-dialog">
          <DialogHeader>
            <DialogTitle>Admin Management — Audit History</DialogTitle>
            <DialogDescription>Recent admin create / update / password actions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {audit.length === 0 ? (
              <p className="py-6 text-center text-sm text-text-muted">No audit entries yet.</p>
            ) : audit.map((a, i) => (
              <div key={i} className="rounded-md border border-border px-3 py-2 text-sm" data-testid={`admin-audit-row-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-text-main">
                    {a.field} · admin #{a.entity_id}
                  </span>
                  <span className="text-xs text-text-muted">{fmtTime(a.at)}</span>
                </div>
                <p className="text-xs text-text-muted">by {a.admin_username || "—"}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
